import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DateIcon from "../../../assets/icons/DateIcon";
import FileIcon from "../../../assets/icons/FileIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import ProfileImg from "../../../assets/images/avatar.svg";
import {
  dateFormat,
  dbDateFormat,
  employeeTypeList,
} from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../features/designation/designationApi";
import {
  useGetEmployeeDetailsQuery,
  useGetEmployeesQuery,
  useUpdateEmployeeOfficialDataMutation,
  useUpdateProfilePictureMutation,
  useUploadPhotoInEmployeeMutation,
} from "../../../features/employee/employeeApi";
import { useGetExpenseUnitsQuery } from "../../../features/expenseUnit/expenseUnitApi";
import { useGetWorkStationsQuery } from "../../../features/workStation/workStationApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { selectArrayOption, selectSearchOption } from "../../../utils/utils";
import InfoTitle from "../../common/InfoTitle";
import SelectImgNameTitle from "../../common/SelectImgNameTitle";
import Loader from "../../ui/Loader";
import UploadProfileImage from "./UploadProfileImage";
import { replaceEditWithView } from "./utils";

export default function OfficialInfo() {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { editId } = useParams();
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  );
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isError: isDesignationError,
    error: designationError,
    data: designationData,
  } = useGetDesignationsQuery({
    params: {
      skip: 0,
      limit: 0,
      ...(departmentId
        ? { business_department_id: departmentId?.toString() }
        : {}),
      is_published: 1,
    },
  });
  const {
    isLoading: isGetDetailsLoading,
    isError: isGetEmployeeDetailsError,
    error: getEmployeeDetailsError,
    data: getEmployeeData,
  } = useGetEmployeeDetailsQuery(editId || "", {
    skip: !editId,
  });
  const [
    updateProfilePicture,
    {
      isLoading: isProfilePicLoading,
      isError: isProfilePicError,
      error: profilePicError,
      isSuccess: isProfilePicSuccess,
    },
  ] = useUpdateProfilePictureMutation();

  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: {
      limit: 0,
      skip: 0,
      status: "active",
    },
  });
  const [updateEmployeeOfficialData, { isLoading, isError, error, isSuccess }] =
    useUpdateEmployeeOfficialDataMutation();
  const [
    uploadPhotoInEmployeeData,
    {
      isLoading: isImgLoading,
      isError: isImgError,
      error: imgError,
      data: imgData,
    },
  ] = useUploadPhotoInEmployeeMutation();
  const {
    isError: isWorkStationError,
    error: workStationError,
    data: workStationData,
  } = useGetWorkStationsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isError: isExpenseUnitError,
    error: expenseUnitError,
    data: expenseUnitData,
  } = useGetExpenseUnitsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation data fail");
  useError(
    isGetEmployeeDetailsError,
    getEmployeeDetailsError,
    "Get employee data fail"
  );
  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(isError, error, "Update official information fail");
  useError(isImgError, imgError, "Profile image upload fail");
  useError(isProfilePicError, profilePicError, "Profile image upload fail");
  useError(isWorkStationError, workStationError, "Get workstation data fail");
  useError(isExpenseUnitError, expenseUnitError, "Get expanse unit data fail");

  const employee = getEmployeeData?.data?.employee;

  const onFinish = (values: any) => {
    const { department_id, join_date, manager_id, ...rest } = values;

    const newData = {
      // ...rest,
      // department: departmentData?.data?.departments?.find(
      //   (department) => department?.id === department_id
      // )?.name,
      // department_id,
      // join_date: join_date ? join_date?.format(dbDateFormat) : undefined,
      // profile_id: employee?.member?.profile?.id,
      // manager_id,
      // old_manager_id: employee?.manager_id,
      rfid: rest?.rfid,
    };

    if (editId)
      updateEmployeeOfficialData({
        body: newData,
        id: editId,
      });
  };

  useEffect(() => {
    if (Object.keys(employee || {})) {
      form.setFieldsValue({
        name: employee?.member?.profile?.name,
        email: employee?.member?.profile?.email,
        department_id: employee?.role?.department?.id,
        business_role_id: employee?.role?.id,
        join_date: employee?.join_date
          ? dayjs(employee?.join_date, dbDateFormat)
          : undefined,
        manager_id: employee?.manager_id,
        employee_id: employee?.employee_id,
        grade: employee?.grade,
        employee_type: employee?.employee_type,
        rfid: employee?.rfid,
        expense_unit:
          getEmployeeData?.data?.expense_unit_details?.id?.toString(),
        work_station:
          getEmployeeData?.data?.work_station_details?.id?.toString(),
      });

      setDepartmentId(employee?.role?.department?.id);
    }
  }, [
    form,
    employee,
    getEmployeeData?.data?.expense_unit_details?.id,
    getEmployeeData?.data?.work_station_details?.id,
  ]);

  const backLink = replaceEditWithView(location.pathname + location.search);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Official information updated successfully!");
      navigate(backLink);
    }
  }, [backLink, isSuccess, navigate]);

  useEffect(() => {
    if (imgData?.Location && editId) {
      updateProfilePicture({
        id: editId,
        body: {
          pro_pic: imgData?.Location,
        },
      });
    }
  }, [editId, imgData?.Location, updateProfilePicture]);

  useEffect(() => {
    if (isProfilePicSuccess) {
      toast("success", "Profile picture updated successfully!");
    }
  }, [isProfilePicSuccess]);

  return (
    <div>
      <InfoTitle className="mb-6" icon={<FileIcon />}>
        Official Information
      </InfoTitle>
      {isGetDetailsLoading ? (
        <Loader />
      ) : (
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <Form.Item className="mb-4">
            <UploadProfileImage
              img={employee?.member?.profile?.pro_pic || ProfileImg}
              handleImage={(file) => uploadPhotoInEmployeeData(file)}
              isLoading={isImgLoading || isProfilePicLoading}
              disabled
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Full Name"
            className="mb-4"
            // rules={[
            //   {
            //     required: true,
            //     message: "Please enter full name",
            //   },
            // ]}
          >
            <Input
              size="large"
              // placeholder="Enter employee name"
              disabled
            />
          </Form.Item>
          <Form.Item name="email" label="Email" className="mb-4">
            {/* Enter email address */}
            <Input
              size="large"
              // placeholder="Email"
              disabled
            />
          </Form.Item>
          <div className="mb-3">
            <Form.Item
              name="department_id"
              label="Department"
              className="mb-1"
              // rules={[
              //   {
              //     required: true,
              //     message: "Please select department",
              //   },
              // ]}
            >
              <Select
                // placeholder="Select"
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  departmentData?.data?.departments || [],
                  "name",
                  "id"
                )}
                suffixIcon={<SelectDropdownLgIcon />}
                onChange={(val) => {
                  setDepartmentId(val);
                  form.setFieldsValue({ business_role_id: undefined });
                }}
                disabled
              />
            </Form.Item>
            {/* <small className="text-sm text-body-text">
              Add departments to tag with co-worker.{" "}
              <Link
                to="/admin/settings/department"
                className="text-accent hover:text-accent hover:underline"
              >
                Add New Department
              </Link>
            </small> */}
          </div>
          <Form.Item
            name="business_role_id"
            label="Designation"
            className="mb-4"
            // rules={[
            //   {
            //     required: true,
            //     message: "Please select designation",
            //   },
            // ]}
          >
            <Select
              // placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDropdownLgIcon />}
              // disabled={!departmentId}
              disabled
            />
          </Form.Item>

          <div className="mb-2.5">
            <Form.Item
              name="join_date"
              label="Joining Date"
              className="mb-1.5"
              // rules={[
              //   {
              //     required: true,
              //     message: "Please select joining date",
              //   },
              // ]}
            >
              <DatePicker
                format={dateFormat as string}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
                disabled
              />
            </Form.Item>
            <small className="block text-body-text text-sm">
              Employee's Leave days will be auto prorated based on their joining
              date. If you want to change the settings,{" "}
              <Link
                to="/admin/settings/leave/leave-prorate"
                className="!text-accent hover:underline cursor-pointer"
              >
                click here
              </Link>
            </small>
          </div>

          <Form.Item name="manager_id" label="Line Manager" className="mb-4">
            <SelectImgNameTitle
              // placeholder="Select"
              data={employeeData?.data?.employees}
              disabled
            />
          </Form.Item>

          <Form.Item
            name="employee_id"
            label="Employee ID"
            className="mb-4"
            // rules={[
            //   // {
            //   //   required: true,
            //   //   message: "Please enter employee ID",
            //   // },
            //   {
            //     validator: async (_: any, value: string) => {
            //       if (!value) {
            //         return Promise.resolve();
            //       }

            //       if (!userIdRegex.test(value)) {
            //         return Promise.reject(
            //           new Error(
            //             "Invalid user ID. No multiple consecutive dots, dashes, or underscores. Must not start or end with special characters"
            //           )
            //         );
            //       }
            //     },
            //   },
            // ]}
          >
            <Input
              size="large"
              // placeholder="Type the ID given to this co-worker"
              disabled
            />
          </Form.Item>
          {/* GS-4 start */}
          <Form.Item name="rfid" label="RFID" className="mb-4">
            <Input size="large" placeholder="Type the RFID" />
          </Form.Item>
          {/* GS-4 end */}
          <Form.Item
            name="grade"
            label="Grade"
            className="mb-4"
            // rules={[
            //   // {
            //   //   required: true,
            //   //   message: "Please enter grade",
            //   // },
            //   {
            //     validator: async (_: any, value: string) => {
            //       if (!value) {
            //         return Promise.resolve();
            //       }

            //       if (Number(value) < 0) {
            //         return Promise.reject(
            //           new Error("Grade cannot be negative")
            //         );
            //       }
            //     },
            //   },
            // ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              size="large"
              // placeholder="Ex: 1"
              disabled
            />
          </Form.Item>

          <Form.Item
            name="employee_type"
            label="Employee Type"
            className="mb-4"
            // rules={[
            //   {
            //     required: true,
            //     message: "Please select employee type",
            //   },
            // ]}
          >
            <Select
              // placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={employeeTypeList}
              suffixIcon={<SelectDropdownLgIcon />}
              disabled
            />
          </Form.Item>

          <Form.Item name="expense_unit" label="Business Unit" className="mb-4">
            {/* <Input
              style={{ width: "100%" }}
              size="large"
              placeholder="Enter expanse unit"
            /> */}
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={
                expenseUnitData?.data?.expenseUnits?.map((item) => ({
                  label: item?.name,
                  value: item?.id?.toString(),
                })) || []
              }
              suffixIcon={<SelectDropdownLgIcon />}
              disabled
            />
          </Form.Item>
          <Form.Item name="work_station" label="Work Station" className="mb-4">
            {/* <Input
              style={{ width: "100%" }}
              size="large"
              placeholder="Enter work station"
            /> */}
            <Select
              // placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={
                workStationData?.data?.workStations?.map((item) => ({
                  label: item?.name,
                  value: item?.id?.toString(),
                })) || []
              }
              suffixIcon={<SelectDropdownLgIcon />}
              disabled
            />
          </Form.Item>

          <Space className="w-full mt-2" size={12}>
            <Button
              className="normal-dark-button modal-button"
              onClick={() => navigate(backLink)}
              size="middle"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              loading={isLoading}
            >
              Update
            </Button>
          </Space>
        </Form>
      )}
    </div>
  );
}
